import { cartWriteRefusal } from "@models/shoppingCart";
import { asApiFailure } from "@utils/apiFailure";

// The storefront's copy for a vendor's stock (#443 / C-07). The count is shown only
// where it limits the shopper — never as a "low stock" hint — so every caller decides
// WHEN; this file only decides the words.

/** The vendor's stock, as the reason the shopper can't have more. */
export const stockLimitMessage = (available: number): string =>
    available > 0
        ? `Only ${available} available from this vendor`
        : "Out of stock from this vendor";

/**
 * A refused Add to Cart: how many MORE this cart can take from the vendor (the API's
 * `addable`), counting what the cart already holds — which the product page cannot see.
 */
export const addableMessage = (addable: number): string =>
    addable > 0
        ? `You can add up to ${addable} more from this vendor.`
        : "This vendor has no more available.";

/** A cart line's refused vendor switch, from the target's `addable`; the line kept its vendor. */
export const switchStockMessage = (vendorName: string, addable: number): string =>
    addable > 0
        ? `You can add up to ${addable} more from ${vendorName}, so this line wasn't switched.`
        : `${vendorName} has no more available, so this line wasn't switched.`;

/** The counts a stock refusal states; `addable` only on add and switch. */
export interface StockRefusal {
    available: number;
    addable?: number;
}

/**
 * A refused cart write, as a line or a product page shows it:
 * - `stock`: worded by the caller from the counts;
 * - `product`: the product, option or sku is gone — the line can only be removed;
 * - `offering`: the vendor no longer offers it — another vendor may;
 * - `limit`: the line would pass the API's per-line limit (PR 339) — no vendor or removal
 *   changes that.
 *
 * The last three carry the API's customer-safe message.
 */
export interface LineRefusal {
    kind: "stock" | "product" | "offering" | "limit";
    message: string;
}

/**
 * The inline answer to a cart write's (add, quantity update, vendor switch) coded 400. undefined for
 * anything else: useApi's filter lets every other failure — a validation failure included
 * — reach its own toast.
 */
export const cartRefusal = (
    error: unknown,
    formatStock: (stock: StockRefusal) => string,
): LineRefusal | undefined => {
    const failure = asApiFailure(error);

    if (failure?.status !== 400) {
        return undefined;
    }

    const refusal = cartWriteRefusal(failure.detail);

    if (!refusal) {
        return undefined;
    }

    if (refusal.kind === "stock") {
        return {
            kind: "stock",
            message: formatStock({
                available: refusal.available,
                addable: refusal.addable,
            }),
        };
    }

    return refusal;
};

/** Why checkout is closed while lines no vendor offers are in the cart (Cart and Checkout). */
export const outOfStockReason = (count: number): string =>
    count === 1
        ? "1 item is out of stock — remove it from your cart."
        : `${count} items are out of stock — remove them from your cart.`;

/** Why checkout is closed while lines are above their vendors' stock (Cart and Checkout). */
export const overStockReason = (count: number): string =>
    count === 1
        ? "1 item is above what its vendor has in stock — lower the quantity or switch vendor in your cart."
        : `${count} items are above what their vendors have in stock — lower the quantities or switch vendors in your cart.`;
